import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@techsofit/app-kit';
import { Button, EmptyState } from '@techsofit/ui';
import { useGymData } from '../data/GymDataProvider';
import { isFreeTrial, isSubscriptionLocked } from '../lib/subscription';

/** Is the gym currently locked out of writes (lapsed / cancelled subscription)? */
export function useSubscriptionLocked(): boolean {
  return isSubscriptionLocked(useGymData().subscription);
}

/**
 * Full-page read-only notice for write screens (record payment, add member) when the
 * subscription has lapsed. Existing data is untouched — only new writes are blocked
 * until the gym renews. {@link action} names what's blocked, e.g. "record payments".
 */
export function SubscriptionLockNotice({ action }: { action: string }) {
  const navigate = useNavigate();
  const { subscription } = useGymData();
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';
  const freeTrial = isFreeTrial(subscription);

  return (
    <section className="animate-fade-up">
      <EmptyState
        icon={<Lock className="size-6" aria-hidden />}
        title={freeTrial ? 'Your free trial has ended' : 'Your subscription has ended'}
        description={`Renew your plan to ${action} again. Your members and data are safe.`}
        action={
          isOwner ? (
            <Button onClick={() => navigate('/billing')}>Renew now</Button>
          ) : (
            <p className="text-sm text-muted">Ask the gym owner to renew.</p>
          )
        }
      />
    </section>
  );
}
